import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { FileText, Flag, CheckCircle2 } from "lucide-react";
import { PROJECT_DOCUMENTS, ProjectKey, DocumentRedFlag } from "@/data/developmentSim";

interface Props {
  projectKey: ProjectKey;
  resolved: Record<string, boolean>; // docId -> correct?
  onResolve: (docId: string, correct: boolean) => void;
}

export default function DocumentReview({ projectKey, resolved, onResolve }: Props) {
  const docs = PROJECT_DOCUMENTS[projectKey];
  return (
    <div className="grid gap-2">
      {docs.map((d) => (
        <DocCard key={d.id} doc={d} resolved={resolved[d.id]} onResolve={(c) => onResolve(d.id, c)} />
      ))}
    </div>
  );
}

function DocCard({ doc, resolved, onResolve }: {
  doc: DocumentRedFlag; resolved: boolean | undefined; onResolve: (correct: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const answered = selected !== null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className={`flex items-center justify-between gap-2 rounded-xl border p-3 text-left transition-all ${
          resolved === true ? "border-emerald-500/40 bg-emerald-500/5"
          : resolved === false ? "border-amber-500/40 bg-amber-500/5"
          : "border-border bg-card/60 hover:border-primary/40"
        }`}>
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-heading text-xs font-bold text-foreground truncate">{doc.title}</p>
              <p className="text-[10px] text-muted-foreground">Tap to review · find the red flag</p>
            </div>
          </div>
          {resolved === true && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
          {resolved === false && <Flag className="w-4 h-4 text-amber-400 shrink-0" />}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-gradient-blue">{doc.title}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-muted/20 p-3 text-[12px] text-foreground/90 leading-relaxed whitespace-pre-line">
            {doc.body}
          </div>
          <p className="font-heading text-sm font-bold text-foreground">{doc.question}</p>
          <div className="space-y-2">
            {doc.options.map((opt, i) => {
              const isSel = selected === i;
              const isCorrect = i === doc.correctIndex;
              const showState = answered && (isSel || isCorrect);
              return (
                <button
                  key={i}
                  disabled={answered}
                  onClick={() => { setSelected(i); onResolve(i === doc.correctIndex); }}
                  className={`w-full rounded-lg border p-2.5 text-left text-[12px] transition-all ${
                    showState && isCorrect ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                    : showState && isSel && !isCorrect ? "border-destructive/50 bg-destructive/10 text-destructive"
                    : "border-border bg-card/60 hover:border-primary/40 text-foreground/85"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {answered && (
            <div className="rounded-xl border border-border bg-card/60 p-3 text-[11px] text-muted-foreground">
              {doc.explanation}
            </div>
          )}
          <Button onClick={() => setOpen(false)} variant="outline" className="w-full">Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}